import { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { getSelectedCatalogItems } from 'entities/CatalogItem/model/selectors/catalogItemSelectors';
import { catalogItemActions } from 'entities/CatalogItem/model/slices/CatalogItemSlice';
import {
    CatalogItem,
    DisplayItem,
    GlobalCatalogItem,
} from 'entities/CatalogItem/model/types/CatalogItemSchema';
import { Category, getLocalCategoriesList } from 'entities/Category';
import { getPropertyVendorsList } from 'entities/Vendors';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { has } from 'shared/lib/lodash/lodash';
import { PropertyCatalogItemDetailed } from 'shared/ui/PropertyCatalogItemDetailed/PropertyCatalogItemDetailed';
import { PropertyCatalogItemsMoveTo } from 'widgets/PropertyCatalog/models/consts/propertyCatalogItemsListTypes';

import { PropertyCatalogItem } from './PropertyCatalogItem';

export interface PropertyCatalogItemDataDefinerProps {
    item: CatalogItem | GlobalCatalogItem;
    quickEdit?: boolean;
    moveTo?: PropertyCatalogItemsMoveTo;
    isDetailed?: boolean;
    isOrderPage?: boolean;
    isSearch?: boolean;
}

const buildCategoryString = (item: GlobalCatalogItem) => {
    if (item.category.parentCategory) {
        return `${item.category.parentCategory.catName} / ${item.category.catName}`;
    }
    return item.category.catName;
};

const buildPropertyCategoryString = (
    item: CatalogItem,
    categories: Record<string, Category>,
) => {
    const categoryName = categories?.[item.categoryId]?.title;
    const glCode = categories?.[item.categoryId]?.glCode;
    if (glCode) {
        return `${categoryName} | ${glCode}`;
    }
    return categoryName;
};

export const PropertyCatalogItemDataDefiner = memo(
    ({
        item,
        quickEdit,
        moveTo,
        isSearch,
        isDetailed,
        isOrderPage,
    }: PropertyCatalogItemDataDefinerProps) => {
        const dispatch = useAppDispatch();
        const selectedItems = useSelector(getSelectedCatalogItems);
        const vendorsList = useSelector(getPropertyVendorsList);
        const categories = useSelector(getLocalCategoriesList);

        const isLocalCatalogItem = 'id' in item;

        const id = isLocalCatalogItem ? item.id : item.uid;
        const uid = isLocalCatalogItem ? item.uuid : undefined;

        const itemIsInOrder = useMemo(
            () =>
                Boolean(
                    selectedItems &&
                        id in selectedItems &&
                        selectedItems?.[id]?.orderItemId,
                ),
            [selectedItems, id],
        );

        const idToEdit = useMemo(
            () => (itemIsInOrder ? selectedItems?.[id].orderItemId : id),
            [itemIsInOrder, selectedItems, id],
        );

        const price = isLocalCatalogItem ? item.price : 0;
        const isInventory = isLocalCatalogItem ? item.isInventory : false;

        const categoryId = isLocalCatalogItem ? item.categoryId : undefined;
        const minAllowedInStock = isLocalCatalogItem
            ? item.minAllowedInStock
            : undefined;

        const isActive = useMemo(
            () => has(selectedItems, id),
            [selectedItems, id],
        );

        const qty = useMemo(
            () => selectedItems?.[id]?.qty,
            [selectedItems, id],
        );

        const displayItem: DisplayItem = useMemo(
            () => ({
                name: 'name' in item ? item.name : item.title,
                category: isLocalCatalogItem
                    ? buildPropertyCategoryString(item, categories)
                    : buildCategoryString(item),
                sku: item.sku,
                uom: item.uom,
                qtyPerUom: item.qtyPerUom,
                uomPrice: item.uomPrice,
                imageUrl: item.imageUrl,
                thumbnailUrl: item.thumbnailUrl,
                manufacturerId: item.manufacturerId,
                mpn: item.mpn,
                upc: item.upc,
                brand: item.brand,
                msrp: item.msrp,
                unspsc: item.unspsc,
                description: item.description,
                descriptionFormat: item.descriptionFormat,
                prop65Warning: item.prop65Warning,
                specifications: item.specifications,
                additionalImages: item.additionalImages,
                proposals: item.proposals,
                glCode: isLocalCatalogItem
                    ? categories?.[item.categoryId]?.glCode
                    : undefined,
            }),
            [item, categories, isLocalCatalogItem],
        );

        const onCancelDetailedView = useCallback(() => {
            dispatch(catalogItemActions.setShowDetailedView(false));
        }, [dispatch]);

        const activatedAccounts = useMemo(() => {
            const result: { [key: string]: boolean[] } = {};

            if (displayItem.proposals && vendorsList) {
                vendorsList.forEach((vendor) => {
                    const uuid = vendor.externalUuid;
                    const foundInProposals = displayItem.proposals.find(
                        (proposal) => proposal.seller.uid === uuid,
                    );

                    if (foundInProposals) {
                        const isOperationsActivated =
                            vendor.isOperationsActivated || false;
                        const isRenovationsActivated =
                            vendor.isRenovationsActivated || false;
                        result[uuid] = [
                            isOperationsActivated,
                            isRenovationsActivated,
                        ];
                    } else {
                        result[uuid] = [false, false];
                    }
                });
            }
            return result;
        }, [displayItem.proposals, vendorsList]);

        return isDetailed ? (
            <PropertyCatalogItemDetailed
                displayItem={displayItem}
                onCancel={onCancelDetailedView}
                price={price}
                moveTo={moveTo}
                id={id}
                isOrderPage={isOrderPage}
                minAllowedInStock={minAllowedInStock}
                isLocal={isLocalCatalogItem}
                activatedAccounts={activatedAccounts}
            />
        ) : (
            <PropertyCatalogItem
                key={id}
                uid={uid}
                isActive={isActive}
                isInOrder={itemIsInOrder}
                isOrderPage={isOrderPage}
                quickEdit={quickEdit}
                moveTo={moveTo}
                id={id}
                categoryId={categoryId}
                idToEdit={idToEdit}
                title={displayItem.name}
                thumbnailUrl={displayItem.thumbnailUrl}
                isSearch={isSearch}
                qty={qty}
                sku={displayItem.sku}
                price={price}
                uom={displayItem.uom}
                qtyPerUom={displayItem.qtyPerUom}
                minAllowedInStock={minAllowedInStock}
                isInventory={isInventory}
                proposals={displayItem.proposals}
                activatedAccounts={activatedAccounts}
            />
        );
    },
);
